import { useCallback, useEffect, useState } from "react";
import { Button, Input, Modal, Table, message } from "antd";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitMenuDto } from "../../../../domain/DataKitDto";
import { DataKitMenuDrawer } from "./DataKitMenuDrawer";
import "./DataKitAccountMenusTab.scss";

interface Props {
  canWrite: boolean;
  onCountChange: (count: number) => void;
}

/**
 * The account-level Menus tab: one list every kit shares.
 *
 * Account-scoped rather than per-kit, which is what lets a rename propagate everywhere — a design
 * can place items from several kits, so a per-kit record would mean one design pointing at two
 * menus of the same name. The kit detail page has its own Menus tab, but that one is a read-only
 * usage view of this list.
 */
export function DataKitAccountMenusTab({ canWrite, onCountChange }: Props) {
  const [menus, setMenus] = useState<DataKitMenuDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<DataKitMenuDto | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    DataKitApi.listMenus()
      .then((res) => {
        const next = res.data || [];
        setMenus(next);
        onCountChange(next.length);
      })
      .catch(() => message.error("Could not load menus"))
      .finally(() => setLoading(false));
  }, [onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  const createMenu = () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    DataKitApi.createMenu({ name, note: newNote.trim() || null })
      .then(() => {
        setCreateOpen(false);
        setNewName("");
        setNewNote("");
        load();
      })
      .catch(() => message.error("Could not create that menu"))
      .finally(() => setSaving(false));
  };

  const columns = [
    {
      title: "Menu",
      dataIndex: "name",
      key: "name",
      sorter: (a: DataKitMenuDto, b: DataKitMenuDto) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      showSorterTooltip: false,
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      render: (note?: string | null) =>
        note ? <span className="menus-note">{note}</span> : <span className="menus-empty">--</span>,
    },
    {
      title: "Designs",
      dataIndex: "designCount",
      key: "designCount",
      width: 92,
      align: "center" as const,
    },
    {
      title: "Items",
      dataIndex: "itemCount",
      key: "itemCount",
      width: 92,
      align: "center" as const,
    },
    {
      title: "Locations",
      dataIndex: "locationCount",
      key: "locationCount",
      width: 92,
      align: "center" as const,
    },
    {
      title: "",
      key: "action",
      width: 85,
      align: "right" as const,
      render: (_: unknown, menu: DataKitMenuDto) => (
        <Button className="menus-edit-btn" size="small" onClick={() => setEditing(menu)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div className="data-kit-account-menus-tab">
      <div className="menus-section-header">
        <span className="menus-eyebrow">Menus</span>
        <span className="menus-caption">
          account-level &mdash; one list every kit shares; designs point at one; channels, tabs, and
          page titles show these names
        </span>
        {canWrite && (
          <Button
            className="menus-new-btn"
            size="small"
            onClick={() => {
              setNewName("");
              setNewNote("");
              setCreateOpen(true);
            }}
          >
            + New Menu
          </Button>
        )}
      </div>

      <Table
        className="menus-table"
        dataSource={menus}
        columns={columns}
        rowKey={(menu) => menu.id || menu.name}
        loading={loading}
        pagination={false}
        locale={{ emptyText: "No menus yet" }}
      />

      <Modal
        className={"data-kit-menu-modal clean one"}
        open={createOpen}
        width={600}
        onCancel={() => setCreateOpen(false)}
      >
        <header>
          <h2>New Menu</h2>
        </header>
        <p className="menus-modal-hint">
          Menu names are the public ones &mdash; they show on website tabs, listings entries and page
          titles.
        </p>
        <label className="data-kit-field">
          <span>Name</span>
          <Input
            size="large"
            placeholder="e.g. Happy Hour"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onPressEnter={createMenu}
            maxLength={255}
            autoFocus
          />
        </label>
        <label className="data-kit-field">
          <span>Note</span>
          <Input
            size="large"
            placeholder="e.g. 3-6pm - bar and patio only"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            maxLength={500}
          />
        </label>

        <footer>
          <Button size="large" onClick={() => setCreateOpen(false)}>
            Cancel
          </Button>
          <Button
            size="large"
            className="primary"
            loading={saving}
            disabled={!newName.trim()}
            onClick={createMenu}
          >
            Create
          </Button>
        </footer>
      </Modal>

      {editing && (
        <DataKitMenuDrawer
          menu={editing}
          canWrite={canWrite}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}
